import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getEmployeeStats, getSyncLogs } from "../services/api";

function StatCard({ label, value, color, icon, to, sub }) {
  return (
    <Link to={to || "/"} style={{ textDecoration: "none" }}>
      <div className="card" style={{
        borderLeft: `4px solid ${color}`, cursor: to ? "pointer" : "default",
        transition: "box-shadow 0.2s"
      }}
        onMouseOver={(e) => { if (to) e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
        onMouseOut={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 36, fontWeight: 700, color }}>{value ?? "—"}</div>
            {sub && <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>{sub}</div>}
          </div>
          <div style={{ fontSize: 36, opacity: 0.3 }}>{icon}</div>
        </div>
      </div>
    </Link>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getEmployeeStats(), getSyncLogs(5)])
      .then(([statsRes, logsRes]) => {
        setStats(statsRes.data);
        setLogs(logsRes.data.logs || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#666" }}>Lade Dashboard...</div>;

  const lastSync = stats?.last_sync
    ? new Date(stats.last_sync).toLocaleString("de-DE")
    : "Noch kein Sync";

  const inSync = (stats?.missing_ad ?? 0) === 0 && (stats?.needs_offboarding ?? 0) === 0;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Dashboard</h1>
        <p style={{ color: "#666", marginTop: 4 }}>Letzter Sync: {lastSync}</p>
      </div>

      {/* Status-Banner */}
      <div style={{
        padding: "14px 20px", borderRadius: 8, marginBottom: 24, fontSize: 15, fontWeight: 600,
        background: inSync ? "#dff6dd" : "#fff4ce",
        color: inSync ? "#107c10" : "#ad7800",
      }}>
        {inSync
          ? "✅ Alles synchron – keine offenen Aktionen."
          : `⚠️ Es gibt offene Aktionen – ${stats?.missing_ad ?? 0} fehlen in Azure AD, ${stats?.needs_offboarding ?? 0} warten auf Offboarding. `}
        {!inSync && <Link to="/sync" style={{ marginLeft: 8 }}>Zur Sync-Vorschau →</Link>}
      </div>

      {/* Stats Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 28 }}>
        <StatCard label="Aktive Mitarbeiter" value={stats?.total_active} color="#0078d4" icon="👥" to="/employees" />
        <StatCard label="Inaktive Mitarbeiter" value={stats?.total_inactive} color="#666" icon="🚪" to="/employees" />
        <StatCard label="Fehlen in Azure AD" value={stats?.missing_ad} color={stats?.missing_ad > 0 ? "#d13438" : "#107c10"} icon="☁️" to="/employees?missing_ad=true" sub="nicht in Entra ID" />
        <StatCard label="Offboarding ausstehend" value={stats?.needs_offboarding} color={stats?.needs_offboarding > 0 ? "#d13438" : "#107c10"} icon="⏳" to="/sync" sub="inaktiv, AD-Konto noch aktiv" />
        <StatCard label="Wartet auf Löschung" value={stats?.awaiting_delete} color={stats?.awaiting_delete > 0 ? "#ff8c00" : "#107c10"} icon="🗑️" to="/employees?awaiting_delete=true" sub="in der Offboarding-Queue" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Quick Actions */}
        <div className="card">
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Schnellaktionen</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Link to="/sync" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#f0f7ff", borderRadius: 8, textDecoration: "none", color: "#0078d4", fontWeight: 500 }}>
              🔄 Sync-Vorschau öffnen
            </Link>
            <Link to="/employees?missing_ad=true" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#fde7e9", borderRadius: 8, textDecoration: "none", color: "#d13438", fontWeight: 500 }}>
              ☁️ Fehlende AD-Nutzer anzeigen
            </Link>
            <Link to="/employees?awaiting_delete=true" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#fff4ce", borderRadius: 8, textDecoration: "none", color: "#ff8c00", fontWeight: 500 }}>
              🗑️ Offboarding-Queue anzeigen
            </Link>
          </div>
        </div>

        {/* Letzte Syncs */}
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>Letzte Syncs</h2>
            <Link to="/logs" style={{ fontSize: 13 }}>Alle anzeigen →</Link>
          </div>
          {logs.length === 0 ? (
            <p style={{ color: "#999", fontSize: 14 }}>Noch keine Syncs durchgeführt</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {logs.map((log) => (
                <div key={log.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                  <div>
                    <span className={`badge badge-${log.status === "success" ? "success" : log.status === "error" ? "danger" : "warning"}`}>
                      {log.status === "success" ? "Erfolgreich" : log.status === "error" ? "Fehler" : "Läuft"}
                    </span>
                    <span style={{ fontSize: 12, color: "#666", marginLeft: 8 }}>
                      {new Date(log.started_at).toLocaleString("de-DE")}
                    </span>
                  </div>
                  <span style={{ fontSize: 12, color: "#999" }}>{log.triggered_by?.split("@")[0]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
